// src/hooks/useGeoEventBus.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

const ZONE_TO_HOLDINGS = {
  "Hormuz":        ["NVDA","AAPL","AMZN","META"],
  "Taiwan":        ["NVDA","AAPL"],
  "Ukraine":       ["MSFT"],
  "S.China Sea":   ["NVDA","AAPL","AMZN"],
  "Bab-el-Mandeb": ["AMZN","META"],
  "Black Sea":     ["MSFT","GOOGL"],
  "Oil shock":     "*",
  "USD":           "*",
};

const EVENT_CONFIG = {
  TANKER_DARK:        { score_delta: 3,  expires_hours: 48  },
  AIS_SPOOF:          { score_delta: 5,  expires_hours: 72  },
  ZONE_ESCALATION:    { score_delta: 8,  expires_hours: 168 },
  CHOKEPOINT_BLOCKED: { score_delta: 12, expires_hours: 96  },
  CYBER_ATTACK:       { score_delta: 6,  expires_hours: 48  },
  DRONE_STRIKE:       { score_delta: 7,  expires_hours: 72  },
  GPS_JAM_ACTIVE:     { score_delta: 4,  expires_hours: 48  },
  PMC_DEPLOYMENT:     { score_delta: 5,  expires_hours: 120 },
  BIOSEC_OUTBREAK:    { score_delta: 6,  expires_hours: 336 },
  DISINFO_SURGE:      { score_delta: 3,  expires_hours: 96  },
  SOVEREIGN_DEFAULT:  { score_delta: 10, expires_hours: 720 },
  CAPITAL_CONTROLS:   { score_delta: 7,  expires_hours: 240 },
  ELECTION_UPSET:     { score_delta: 5,  expires_hours: 168 },
  REGIME_CHANGE:      { score_delta: 9,  expires_hours: 336 },
};

const BASE_SCORE = 40;

function getHoldingsForZone(zone, portfolioTickers) {
  const mapping = ZONE_TO_HOLDINGS[zone];
  if (!mapping) return [];
  if (mapping === "*") return portfolioTickers;
  return mapping.filter(t => portfolioTickers.includes(t));
}

let localListeners = {};
function localEmit(event) {
  const handlers = localListeners[event.type] || [];
  handlers.forEach(h => h(event));
  const allHandlers = localListeners['*'] || [];
  allHandlers.forEach(h => h(event));
}
function localOn(type, handler) {
  if (!localListeners[type]) localListeners[type] = [];
  localListeners[type].push(handler);
  return () => {
    localListeners[type] = localListeners[type].filter(h => h !== handler);
  };
}

export function useGeoEventBus({ portfolio = [] } = {}) {
  const portfolioTickers = portfolio.map(h => h.ticker || h.symbol || h.stock).filter(Boolean);
  const [activeEvents, setActiveEvents] = useState([]);
  const [geoScore, setGeoScore] = useState(BASE_SCORE);
  const channelRef = useRef(null);

  useEffect(() => {
    if (!supabase) return;
    const ch = supabase.channel('nuvport-geo-events', { config: { broadcast: { self: false } } });
    ch.on('broadcast', { event: 'geo_event' }, ({ payload }) => {
      handleIncomingEvent(payload);
    }).subscribe();
    channelRef.current = ch;
    return () => { supabase.removeChannel(ch); };
  // eslint-disable-next-line
  }, []);

  const handleIncomingEvent = useCallback((evt) => {
    setActiveEvents(prev => {
      const filtered = prev.filter(e => e.id !== evt.id && new Date(e.expires_at) > new Date());
      return [...filtered, evt];
    });
    localEmit(evt);
  }, []);

  useEffect(() => {
    const now = new Date();
    const valid = activeEvents.filter(e => new Date(e.expires_at) > now);
    const total = valid.reduce((sum, e) => sum + (e.score_delta || 0), BASE_SCORE);
    setGeoScore(Math.min(100, Math.max(0, total)));
  }, [activeEvents]);

  const fire = useCallback((type, zone, payload = {}) => {
    const cfg = EVENT_CONFIG[type] || { score_delta: 5, expires_hours: 48 };
    const expiresAt = new Date(Date.now() + cfg.expires_hours * 3600 * 1000).toISOString();
    const evt = {
      id: `evt_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      type,
      zone,
      severity: cfg.score_delta >= 10 ? 'CRITICAL' : cfg.score_delta >= 7 ? 'HIGH' : cfg.score_delta >= 5 ? 'MEDIUM' : 'LOW',
      holdings_affected: getHoldingsForZone(zone, portfolioTickers),
      score_delta: cfg.score_delta,
      expires_at: expiresAt,
      payload,
      timestamp: new Date().toISOString(),
    };
    if (channelRef.current) {
      channelRef.current.send({ type: 'broadcast', event: 'geo_event', payload: evt });
    }
    handleIncomingEvent(evt);
  }, [portfolioTickers, handleIncomingEvent]);

  const on = useCallback((type, handler) => {
    return localOn(type, handler);
  }, []);

  return { fire, on, geoScore, activeEvents, ZONE_TO_HOLDINGS };
}

export { ZONE_TO_HOLDINGS, EVENT_CONFIG };
